/**
 * Space Worm — which bite is the right one (specs/space-worm-mode.md §5.3, §5.4, §7.2).
 *
 * The board is a word and a pile of tiles. The only question this module answers is "is the letter
 * the head just entered the one this word wants next", and the only subtlety in it is duplicates:
 * `Blatt` places two `t` tiles and **either** satisfies the step that wants a `t`. So the board
 * tracks how many of each glyph are still due — never tile identity — because a child who eats the
 * nearer of two identical letters has not made a mistake.
 *
 * Phaser-free. The word itself (steps, Korean blocks, Thai clusters) comes from `cat/word.ts`,
 * which is the whole reason this mode costs no new content in any language.
 */
import type { DoorWord } from '@/game/cat/word';
import { renderTrain } from '@/game/cat/word';
import { dist, type Pt, type Bounds } from './step';

export interface WormTile {
  /** Stable within a board; the scene keys its art off this. */
  id: number;
  glyph: string;
  /** Where it sits, in pixels. There is no grid in this mode (§5.1). */
  x: number;
  y: number;
  /** True when this tile is one of the word's own letters (§7.3 — decoys are never in the word). */
  target: boolean;
}

export interface WormBoard {
  /** 1-based rung of the run's ladder (§4.4). Only a FINISHED word advances it. */
  rung: number;
  word: DoorWord;
  tiles: WormTile[];
  /** How many steps of the word are already in the worm. */
  eaten: number;
}

/** The glyph the word wants next, or null when the word is finished. */
export function nextGlyph(b: WormBoard): string | null {
  return b.eaten < b.word.steps.length ? b.word.steps[b.eaten] : null;
}

export function isComplete(b: WormBoard): boolean {
  return b.eaten >= b.word.steps.length;
}

/**
 * The tile the head is touching — the NEAREST one, if it is somehow touching two.
 *
 * Touching a letter is eating it (§5.4): there is no hover-don't-swallow rule, because it would be
 * invisible, unteachable, and an argument about every near miss.
 */
export function tileAt(b: WormBoard, head: Pt, radius: number, bounds: Bounds): WormTile | null {
  let best: WormTile | null = null;
  let bestD = Infinity;
  for (const t of b.tiles) {
    const d = dist(head, t, bounds);
    if (d < radius && d < bestD) { best = t; bestD = d; }
  }
  return best;
}

export type Bite = 'right' | 'wrong';

/**
 * Eat a tile.
 *
 * `'right'` removes it and advances the word. `'wrong'` changes nothing here — the scene answers it
 * by spending a book and throwing the whole word away (§5.4), so there is no half-eaten board left
 * behind to be stuck on.
 */
export function bite(b: WormBoard, tile: WormTile): Bite {
  if (!tile.target || tile.glyph !== nextGlyph(b)) return 'wrong';
  b.tiles = b.tiles.filter((t) => t.id !== tile.id);
  b.eaten += 1;
  return 'right';
}

/**
 * The word as the worm currently carries it — the beads, and the slots under the picture.
 *
 * `renderTrain` is what makes Korean work: a half-collected block shows as the jamo so far and
 * snaps to the composed syllable the moment its vowel arrives (§8.2).
 */
export function spelledSoFar(b: WormBoard): string {
  return renderTrain(b.word, b.eaten);
}

/** The glyph carried by bead `i` of the worm's body, head-first, for the beads that carry one. */
export function beadGlyphs(b: WormBoard): string[] {
  return b.word.steps.slice(0, b.eaten);
}
